import { ReactNode } from 'react';

import { Field } from 'react-final-form';

import { MultiAutocompleteBadgeCount } from '@cast/design-system';
import type { MultiAutocompleteBadgeCountProps } from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { showErrorOnChange } from '../utils';

type OmitMultiAutocompleteBadgeCountProps<Item> = Omit<
  MultiAutocompleteBadgeCountProps<Item>,
  'value' | 'renderSelectedItems'
>;

type Props<Item> = {
  name: string;
  renderError?: (error: string | string[]) => ReactNode;
} & OmitMultiAutocompleteBadgeCountProps<Item>;

export const RffMultiAutocompleteBadgeCount = <Item,>({
  disabled: propsDisabled,
  readOnly: propsReadOnly,
  name: propsName,
  onChange: propsOnChange,
  renderError,
  ...props
}: Props<Item>) => {
  const { mode } = useRffFormState();
  const { disabled, readOnly, name } = useControlsGroupState({
    disabled: propsDisabled,
    readOnly: propsReadOnly ?? mode === 'view',
    name: propsName,
  });

  return (
    <Field<string[]> name={name || propsName}>
      {({ input: { onChange, value }, meta }) => (
        // @ts-expect-error todo: come up with a better type solution
        <MultiAutocompleteBadgeCount
          {...(props as unknown as OmitMultiAutocompleteBadgeCountProps<Item>)}
          value={value || []}
          onChange={({ values, selectedItems }) => {
            onChange(values);
            propsOnChange?.({ values, selectedItems });
          }}
          disabled={disabled}
          readOnly={readOnly}
          success={meta.touched && meta.valid}
          error={
            showErrorOnChange(meta)
              ? renderError?.(meta.error) || meta.error
              : undefined
          }
        />
      )}
    </Field>
  );
};
